import React, { useRef } from 'react';

export default function FooterSection({ onSignIn, onGetStarted }) {
  const marqueeRef = useRef(null);

  const handleScrollToWaitlist = () => {
    const el = document.getElementById('waitlist');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMarqueeEnter = () => {
    if (marqueeRef.current) {
      marqueeRef.current.style.animationPlayState = 'paused';
    }
  };
  const handleMarqueeLeave = () => {
    if (marqueeRef.current) {
      marqueeRef.current.style.animationPlayState = 'running';
    }
  };

  // Repeated text for seamless loop
  const marqueeText = Array(6).fill(null).map((_, i) => (
    <span key={i} className="flex items-center gap-4 mr-12 shrink-0">
      <span
        className="font-display-gz font-bold tracking-tight text-brand-gz-purple"
        style={{ fontSize: 'clamp(36px, 5vw, 64px)' }}
      >
        RESUME
      </span>
      <span
        className="font-display-gz font-bold tracking-tight"
        style={{ fontSize: 'clamp(36px, 5vw, 64px)', color: 'var(--lp-marquee-text)' }}
      >
        BAR
      </span>
      <span className="text-2xl select-none" style={{ color: 'var(--lp-marquee-text)' }} aria-hidden="true">✦</span>
    </span>
  ));

  return (
    <footer
      className="pt-20 pb-12 overflow-hidden transition-colors duration-300"
      style={{ background: 'var(--lp-bg)' }}
    >

      {/* ── Animated Marquee Strip ── */}
      <div
        className="w-full overflow-hidden py-5 mb-16 cursor-default"
        style={{ borderTop: '1px solid var(--lp-border)', borderBottom: '1px solid var(--lp-border)' }}
        onMouseEnter={handleMarqueeEnter}
        onMouseLeave={handleMarqueeLeave}
      >
        <div
          ref={marqueeRef}
          className="flex whitespace-nowrap select-none"
          style={{
            width: 'max-content',
            animation: 'marquee-reverse 18s linear infinite',
          }}
        >
          {marqueeText}
        </div>
      </div>

      {/* ── Footer Content ── */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr] gap-12">

          {/* Col 1: Brand */}
          <div className="flex flex-col items-start">
            <img
              src="/resumebar-logo-dark.svg"
              alt="Resumebar Logo"
              className="h-8 select-none"
            />
            <p className="text-sm mt-3 max-w-xs leading-relaxed" style={{ color: 'var(--lp-text-muted)' }}>
              Built for Indian students and professionals who deserve better than boring resume templates.
            </p>
            <button
              onClick={handleScrollToWaitlist}
              className="mt-6 bg-brand-gz-purple text-[#14213D] text-sm font-bold px-5 py-2.5 rounded-lg hover:bg-brand-gz-purple-light transition-colors duration-200 select-none"
            >
              Join the Waitlist →
            </button>
          </div>

          {/* Col 2: Product */}
          <div className="flex flex-col items-start">
            <h4 className="text-xs font-medium uppercase tracking-widest mb-5 select-none" style={{ color: 'var(--lp-text-muted)' }}>
              Product
            </h4>
            <div className="flex flex-col gap-3">
              {['Build Resume', 'Templates', 'ATS Checker'].map((link) => (
                <button
                  key={link}
                  onClick={handleScrollToWaitlist}
                  className="text-sm transition-colors duration-150 text-left select-none"
                  style={{ color: 'var(--lp-text-muted)' }}
                  onMouseEnter={e => e.target.style.color = 'var(--lp-text)'}
                  onMouseLeave={e => e.target.style.color = 'var(--lp-text-muted)'}
                >
                  {link}
                </button>
              ))}
              <a
                href="/plans"
                className="text-sm transition-colors duration-150 text-left select-none"
                style={{ color: 'var(--lp-text-muted)', textDecoration: 'none' }}
                onMouseEnter={e => e.target.style.color = 'var(--lp-text)'}
                onMouseLeave={e => e.target.style.color = 'var(--lp-text-muted)'}
              >
                Pricing
              </a>
            </div>
          </div>

          {/* Col 3: Company */}
          <div className="flex flex-col items-start">
            <h4 className="text-xs font-medium uppercase tracking-widest mb-5 select-none" style={{ color: 'var(--lp-text-muted)' }}>
              Company
            </h4>
            <div className="flex flex-col gap-3">
              <a
                href="/about"
                className="text-sm transition-colors duration-150 cursor-pointer select-none"
                style={{ color: 'var(--lp-text-muted)', textDecoration: 'none' }}
                onMouseEnter={e => e.target.style.color = 'var(--lp-text)'}
                onMouseLeave={e => e.target.style.color = 'var(--lp-text-muted)'}
              >
                About
              </a>
              <a
                href="/help"
                className="text-sm transition-colors duration-150 cursor-pointer select-none"
                style={{ color: 'var(--lp-text-muted)', textDecoration: 'none' }}
                onMouseEnter={e => e.target.style.color = 'var(--lp-text)'}
                onMouseLeave={e => e.target.style.color = 'var(--lp-text-muted)'}
              >
                Help & FAQ
              </a>
              {['Blog', 'Careers'].map((link) => (
                <span
                  key={link}
                  className="text-sm transition-colors duration-150 cursor-pointer select-none"
                  style={{ color: 'var(--lp-text-muted)' }}
                  onMouseEnter={e => e.target.style.color = 'var(--lp-text)'}
                  onMouseLeave={e => e.target.style.color = 'var(--lp-text-muted)'}
                >
                  {link}
                </span>
              ))}
            </div>
          </div>

          {/* Col 4: Legal */}
          <div className="flex flex-col items-start">
            <h4 className="text-xs font-medium uppercase tracking-widest mb-5 select-none" style={{ color: 'var(--lp-text-muted)' }}>
              Legal
            </h4>
            <div className="flex flex-col gap-3">
              <a
                href="/privacy"
                className="text-sm transition-colors duration-150 cursor-pointer select-none"
                style={{ color: 'var(--lp-text-muted)', textDecoration: 'none' }}
                onMouseEnter={e => e.target.style.color = 'var(--lp-text)'}
                onMouseLeave={e => e.target.style.color = 'var(--lp-text-muted)'}
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className="text-sm transition-colors duration-150 cursor-pointer select-none"
                style={{ color: 'var(--lp-text-muted)', textDecoration: 'none' }}
                onMouseEnter={e => e.target.style.color = 'var(--lp-text)'}
                onMouseLeave={e => e.target.style.color = 'var(--lp-text-muted)'}
              >
                Terms of Service
              </a>
              <a
                href="/refund-policy"
                className="text-sm transition-colors duration-150 cursor-pointer select-none"
                style={{ color: 'var(--lp-text-muted)', textDecoration: 'none' }}
                onMouseEnter={e => e.target.style.color = 'var(--lp-text)'}
                onMouseLeave={e => e.target.style.color = 'var(--lp-text-muted)'}
              >
                Refund Policy
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="mt-16 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderTop: '1px solid var(--lp-border)' }}
        >
          <p className="text-xs select-none" style={{ color: 'var(--lp-text-muted)' }}>
            © {new Date().getFullYear()} Resumebar. Made with <span className="text-brand-gz-purple">✦</span> in India.
          </p>
          <p className="text-xs select-none" style={{ color: 'var(--lp-text-muted)' }}>
            v2.0 · Built for Gen Z
          </p>
        </div>
      </div>
    </footer>
  );
}
